import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { SensorMapMarkersService } from '@app/sensordaten/services/sensor-map-markers.service';
import { AuthService } from '@app/shared/services/auth.service';
import { Subscription } from 'rxjs';
import { NewLegendsService } from './new-legends.service';
import { ProducerEnergyTypes } from '@app/shared/enums';

type VisibilityKey =
  | 'isConsumersVisible'
  | 'isProducersVisible'
  | 'isPhotovoltaikVisible'
  | 'isWindVisible'
  | 'isWasserVisible'
  | 'isBiomasseVisible'
  | 'isStreetLightsVisible'
  | 'isStreetLightsHeatmap'
  | 'isIotVisible'
  | 'isConsumerEfficiencyVisible';

@Component({
  selector: 'app-legends-new',
  templateUrl: './new-legends.component.html',
  styleUrls: ['./new-legends.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NewLegendsComponent implements OnDestroy {
  legends = [
    {
      type: 'lampLegend',
      title: 'Straßenbeleuchtung',
      content: [
        {
          name: 'Effizient',
          class: 'marker marker-green-outer-white marker-circular',
        },
        {
          name: 'Durchschnittlich Effizient',
          class: 'marker marker-yellow-outer-white marker-circular',
        },
        {
          name: 'Ineffizient',
          class: 'marker marker-red-outer-white marker-circular',
        },
        {
          name: 'Spezialbeleuchtung',
          class: 'marker marker-black-outer-white marker-circular',
        },
      ],
    },
  ];

  public visibility: {
    isConsumersVisible: boolean;
    isProducersVisible: boolean;
    isPhotovoltaikVisible: boolean;
    isWindVisible: boolean;
    isWasserVisible: boolean;
    isBiomasseVisible: boolean;
    isStreetLightsVisible: boolean;
    isStreetLightsHeatmap: boolean;
    isIotVisible: boolean;
    isConsumerEfficiencyVisible: boolean;
  };

  public availableProducers: string[];

  private readonly producerChildren: VisibilityKey[] = ['isPhotovoltaikVisible', 'isWindVisible', 'isWasserVisible', 'isBiomasseVisible'];

  private iotSubscription: Subscription;

  constructor(public authService: AuthService, public legendsService: NewLegendsService, private sensorMapMarkersService: SensorMapMarkersService) {
    this.visibility = {
      isConsumersVisible: this.legendsService.isConsumersVisible,
      isProducersVisible: this.legendsService.isProducersVisible,
      isPhotovoltaikVisible: this.legendsService.isPhotovoltaikVisible,
      isWindVisible: this.legendsService.isWindVisible,
      isWasserVisible: this.legendsService.isWasserVisible,
      isBiomasseVisible: this.legendsService.isBiomasseVisible,
      isStreetLightsVisible: this.legendsService.isStreetLightsVisible,
      isStreetLightsHeatmap: this.legendsService.isStreetLightsHeatmap,
      isConsumerEfficiencyVisible: this.legendsService.isConsumerEfficiencyVisible,
      isIotVisible: this.legendsService.isIotVisible,
    };

    this.availableProducers = this.legendsService.availableProducers;
    this.iotSubscription = this.sensorMapMarkersService.isVisible.subscribe((isVisible) => (this.visibility.isIotVisible = isVisible));
  }

  ngOnDestroy() {
    this.iotSubscription.unsubscribe();
  }

  public toggleIcons(display: string) {
    switch (display) {
      case 'consumers':
        this.updateVisibility('isConsumersVisible', !this.visibility.isConsumersVisible);
        this.legendsService.toggleConsumersLayer();
        break;

      case 'producers':
        const newProducerValue = !this.visibility.isProducersVisible;
        this.updateVisibility('isProducersVisible', newProducerValue);
        this.producerChildren.forEach((childKey) => this.updateVisibility(childKey, newProducerValue));
        this.legendsService.toggleAllProducerLayer();
        break;

      case ProducerEnergyTypes.PHOTOVOLTAICS:
      case ProducerEnergyTypes.BIOMASS:
      case ProducerEnergyTypes.WATER:
      case ProducerEnergyTypes.WIND:
        const producerChildKey: VisibilityKey = `is${display}Visible`;
        this.updateVisibility(producerChildKey, !this.visibility[producerChildKey]);
        const availableKeys = this.availableProducers.map((p) => `is${p}Visible`);
        const producerParentIsVisible = availableKeys.some((key: string) => this.visibility[key as VisibilityKey]);
        this.updateVisibility('isProducersVisible', producerParentIsVisible);
        this.legendsService.toggleProducerLayer(this.visibility[producerChildKey], display);
        break;

      case 'streetlights':
        this.updateVisibility('isStreetLightsVisible', !this.visibility.isStreetLightsVisible);
        this.legendsService.toggleStreetlightsLayer();
        break;

      case 'heatmap':
        this.updateVisibility('isStreetLightsHeatmap', !this.visibility.isStreetLightsHeatmap);
        this.legendsService.toggleStreetlightsLayer();
        break;

      case 'consumerEfficiency':
        this.updateVisibility('isConsumerEfficiencyVisible', !this.visibility.isConsumerEfficiencyVisible);
        this.legendsService.toggleConsumerEfficiencyLayer();
        break;

      case 'iot':
        this.updateVisibility('isIotVisible', !this.visibility.isIotVisible);
        this.sensorMapMarkersService.toggle();
        break;
    }
  }

  private updateVisibility(key: VisibilityKey, value: boolean): void {
    if (this.visibility[key] === value) {
      return;
    }
    this.visibility[key] = value;
    this.legendsService.updatePreferences({ [key]: value });
    (this.legendsService as any)[`_${key}$`]?.next(value);
  }

  trackByFnContent(_: number, item: any) {
    return item.name;
  }

  trackByFnLegends(_: number, item: any) {
    return item.title;
  }
}
